'use client';

import { FC } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import {
  ProductHero,
  TrustBar,
  ProductFAQ,
  ProductCTAFooter,
} from '../components';

// Feature card component
const FeatureCard: FC<{
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  compactImage?: boolean; // Reduce bottom padding for cards with extra whitespace in image
}> = ({ title, description, image, imageAlt, compactImage }) => (
  <div
    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 lg:p-10"
    style={compactImage ? { paddingBottom: '24px' } : undefined}
  >
    <div className="mb-6">
      <h3
        className="font-medium text-black mb-3"
        style={{ fontSize: 'clamp(1.25rem, 2vw, 2rem)' }}
      >
        {title}
      </h3>
      <p
        className="text-slate-600"
        style={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)' }}
      >
        {description}
      </p>
    </div>
    <div className="rounded-xl overflow-hidden bg-white shadow-sm">
      <Image
        src={image}
        alt={imageAlt}
        width={1200}
        height={600}
        className="w-full h-auto object-contain"
      />
    </div>
  </div>
);

const EMAIL_FEATURES = {
  aiPersonalization: {
    title: 'AI-written personalization that sounds human',
    description: 'Every email is crafted using company data, recent activity, and prospect context. Not just {{firstName}} — real research woven into every line so prospects actually reply.',
    image: '/images/products/daily-playbook/hero.jpg',
    imageAlt: 'AI-personalized email sequence interface',
  },
  multiMailbox: {
    title: 'Multi-mailbox rotation to protect deliverability',
    description: 'Spread sends across multiple mailboxes automatically. Your domain reputation stays clean, inbox placement stays high, and you never hit send limits.',
    image: '/images/products/daily-playbook/hero.jpg',
    imageAlt: 'Multi-mailbox rotation dashboard',
  },
  smartSendTimes: {
    title: 'Smart send times for every recipient',
    description: 'AI picks the optimal send time based on timezone, historical open patterns, and day-of-week trends. Stop guessing — let data decide when your email lands.',
    image: '/images/products/daily-playbook/hero.jpg',
    imageAlt: 'Smart send time optimization interface',
  },
  replyDetection: {
    title: 'Reply detection that routes hot leads instantly',
    description: 'Automatically detect replies, classify sentiment, and route interested buyers to the right SDR. Positive replies trigger alerts — negative ones pause the sequence.',
    image: '/images/products/daily-playbook/hero.jpg',
    imageAlt: 'Reply detection and routing interface',
  },
  abTesting: {
    title: 'A/B testing at scale with real statistical rigor',
    description: 'Test subject lines, CTAs, message length, and send times across thousands of sends. Statistical significance is calculated automatically — no spreadsheets required.',
    image: '/images/products/daily-playbook/hero.jpg',
    imageAlt: 'A/B testing results dashboard',
  },
  deliverabilityDashboard: {
    title: 'Deliverability dashboard you actually understand',
    description: 'Monitor bounce rates, spam complaints, and domain reputation in real-time. Get alerts before problems tank your campaigns — not after.',
    image: '/images/products/daily-playbook/hero.jpg',
    imageAlt: 'Email deliverability monitoring dashboard',
  },
};

const EMAIL_FAQS = [
  {
    question: 'How does AI personalization work without sounding robotic?',
    answer: 'Our AI agents pull context from company websites, LinkedIn profiles, recent news, and your CRM data. They generate unique snippets for each prospect — not templates with swapped variables. Reps can review and edit before sending.',
  },
  {
    question: 'Can I import contacts from HubSpot, Salesforce, or CSV?',
    answer: 'Yes. Import from your CRM, upload CSV files, or build audiences directly from website visitors. All sources can be combined, deduplicated, and enriched automatically before your first send.',
  },
  {
    question: 'How does multi-mailbox rotation protect my domain?',
    answer: 'Sends are distributed across multiple connected mailboxes so no single address exceeds provider limits. Combined with warm-up schedules and send throttling, your domain reputation stays healthy even at high volume.',
  },
  {
    question: 'Can I mix email, calls, and LinkedIn in the same sequence?',
    answer: 'Absolutely. Sequences support multi-channel workflows — emails, call tasks, LinkedIn touches, and custom steps all in one flow. Each channel fires at the right time based on prospect engagement.',
  },
  {
    question: 'How do replies automatically create follow-ups?',
    answer: 'When a prospect replies, our system classifies the sentiment in real-time. Positive replies alert the assigned SDR and pause the sequence. Objections can trigger a tailored follow-up. Out-of-office replies reschedule automatically.',
  },
  {
    question: 'What deliverability metrics can I track?',
    answer: 'You get real-time visibility into open rates, bounce rates, spam complaints, unsubscribe rates, and domain reputation scores. Alerts fire when any metric crosses a threshold so you can fix issues before they snowball.',
  },
];

const EmailsPage: FC = () => {
  return (
    <div className="bg-white">
      <ProductHero
        iconSrc="/images/products/icon-emails.svg"
        label="Email Automation"
        title="Send emails that actually get replies"
        highlightedWord="replies"
        subtitle="AI-powered sequences that research, personalize, and optimize every send — so your SDRs focus on conversations, not copy-paste."
        primaryCTA={{
          label: 'Book a demo',
          href: '/book-demo',
        }}
      />

      <TrustBar />

      {/* Custom Feature Section with specific layout */}
      <section className="py-16 md:py-24">
        <div className="container-fluid px-4 md:px-7.5 mx-auto" style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}>
          {/* Section Title */}
          <FadeIn variant="up" delay={0.1}>
            <div className="text-center mb-12">
              <h2
                className="font-medium text-black"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 3rem)' }}
              >
                Outbound email that <span style={{ color: '#094FFE' }}>scales</span> without losing the human touch
              </h2>
            </div>
          </FadeIn>

          {/* Features with alternating full-width and 2-col layout */}
          <div className="space-y-6 md:space-y-8">
            {/* Row 1: Full width - AI Personalization */}
            <FadeIn variant="up" delay={0.15}>
              <FeatureCard {...EMAIL_FEATURES.aiPersonalization} />
            </FadeIn>

            {/* Row 2: Two columns - Multi-Mailbox + Smart Send Times */}
            <FadeIn variant="up" delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
                <FeatureCard {...EMAIL_FEATURES.multiMailbox} compactImage />
                <FeatureCard {...EMAIL_FEATURES.smartSendTimes} />
              </div>
            </FadeIn>

            {/* Row 3: Full width - Reply Detection */}
            <FadeIn variant="up" delay={0.25}>
              <FeatureCard {...EMAIL_FEATURES.replyDetection} />
            </FadeIn>

            {/* Row 4: Two columns - A/B Testing + Deliverability Dashboard */}
            <FadeIn variant="up" delay={0.3}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
                <FeatureCard {...EMAIL_FEATURES.abTesting} compactImage />
                <FeatureCard {...EMAIL_FEATURES.deliverabilityDashboard} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <ProductFAQ faqs={EMAIL_FAQS} />

      <ProductCTAFooter
        productName="Email Automation"
        title="Turn any list into pipeline"
        subtitle="with sequences that feel hand-written"
      />
    </div>
  );
};

export default EmailsPage;
